export function formatDate(date: string | Date, timezone?: string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };
  if (timezone) {
    options.timeZone = timezone;
  }
  return d.toLocaleDateString("en-GB", options);
}

export function formatDateTime(date: string | Date, timezone?: string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };
  if (timezone) {
    options.timeZone = timezone;
  }
  return d.toLocaleString("en-GB", options);
}

type YearMonthDay = { year: number; month: number; day: number };

/** Parses a leading YYYY-MM-DD from an ISO date or date-time string. */
function parseIsoDateLeading(iso: string): YearMonthDay | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return null;
  }
  return { year, month, day };
}

function calendarYmdInTimeZone(
  isoDateTime: string,
  timeZone: string,
): YearMonthDay | null {
  const instant = new Date(isoDateTime);
  if (Number.isNaN(instant.getTime())) return null;
  try {
    const formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      year: "numeric",
      month: "numeric",
      day: "numeric",
    });
    const parts = formatter.formatToParts(instant);
    const num = (type: Intl.DateTimeFormatPartTypes) => {
      const p = parts.find((x) => x.type === type)?.value;
      return p !== undefined ? Number(p) : NaN;
    };
    const year = num("year");
    const month = num("month");
    const day = num("day");
    if (![year, month, day].every((n) => Number.isFinite(n))) return null;
    return { year, month, day };
  } catch {
    return null;
  }
}

/**
 * Full years old on the calendar date of `eventStartsAt` in `eventTimeZone`,
 * comparing to date-of-birth as a calendar date (YYYY-MM-DD).
 */
export function ageInYearsAtEventStart(
  dateOfBirthIso: string,
  eventStartsAtIso: string,
  eventTimeZone: string,
): number | null {
  const birth = parseIsoDateLeading(dateOfBirthIso);
  const eventDay = calendarYmdInTimeZone(eventStartsAtIso, eventTimeZone);
  if (!birth || !eventDay) return null;
  let age = eventDay.year - birth.year;
  if (
    eventDay.month < birth.month ||
    (eventDay.month === birth.month && eventDay.day < birth.day)
  ) {
    age -= 1;
  }
  if (age < 0 || age > 130) return null;
  return age;
}

/** True if any consent-form medical / dietary field has non-whitespace content. */
export function participantHasMedicalInformation(p: {
  consentFormImportantMedicalInformation?: string | null;
  consentFormMedicationDetails?: string | null;
  consentFormInjuriesDetails?: string | null;
  consentFormDietaryRequirementsDetails?: string | null;
  consentFormDisabilityDetails?: string | null;
}): boolean {
  const values = [
    p.consentFormImportantMedicalInformation,
    p.consentFormMedicationDetails,
    p.consentFormInjuriesDetails,
    p.consentFormDietaryRequirementsDetails,
    p.consentFormDisabilityDetails,
  ];
  return values.some((v) => v != null && v.trim() !== "");
}
